import Link from "next/link";
import { useEffect, useMemo, useRef } from "react";
import { LuExternalLink } from "react-icons/lu";

import { DIALOG, setDialog } from "@/components/app/dialogs/dialog-provider";
import MarketCountdown from "@/components/shared/creator-coin-exchange-modal/market-countdown";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useLayout } from "@/contexts/layout-context";
import { useCoinPropertiesQuery } from "@/hooks/use-coin-properties-query";
import {
  APP_NAME_GENERIC,
  DESO_TOKEN_DISPLAY_NAME,
  OPENFUND_APP_NAME,
} from "@/utils/constants";
import { getVisiblePrice } from "@/utils/currency";

import { InfoTooltip } from "../info-tooltip";
import { TriggerLoggedInModalWindow } from "../is-logged-in";
import { CreatorCoinTab } from "./creator-coin-tabs";

type CreatorCoinExchangeModalProps = {
  publicKey: string;
};

const profileCoinActionButtonClassName =
  "h-7 gap-1.5 px-3 py-0 text-sm leading-none";

const CreatorCoinExchangeWrapper = ({
  publicKey,
}: CreatorCoinExchangeModalProps) => {
  const { data, isLoading } = useCoinPropertiesQuery({ publicKey });
  const { setDynamicTitle } = useLayout();

  const coinPrice = data?.coinPrice ?? null;
  const coinRunAtDate = data?.coinProperties?.RunAt?.[0] ?? null;
  const ammRunning = data?.ammRunning;
  const coinPriceWrapperRef = useRef<HTMLDivElement>(null!);

  const visiblePrice = useMemo(() => {
    if (!coinPrice) {
      return 0;
    }

    return getVisiblePrice(coinPrice);
  }, [coinPrice]);

  const tradeOnOpenfundButton = useMemo(() => {
    return (
      <Link
        href={`${process.env.NEXT_PUBLIC_OPENFUND_URI}/trade/${data?.coinProperties.Profile.Username}`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex h-7 shrink-0"
      >
        <Button
          size="xs"
          variant="outline"
          className={profileCoinActionButtonClassName}
        >
          {OPENFUND_APP_NAME}
          <LuExternalLink className="size-3.5 shrink-0" />
        </Button>
      </Link>
    );
  }, [data]);

  useEffect(() => {
    return setDynamicTitle((originalTitle) => {
      const coinPriceText =
        coinPriceWrapperRef.current?.querySelector(".coin-price")?.textContent;

      if (visiblePrice && coinPriceText) {
        return `${coinPriceText} ${originalTitle}`;
      }
      return originalTitle;
    });
  }, [setDynamicTitle, visiblePrice]);

  if (isLoading) {
    return null;
  }

  if (!coinRunAtDate && !coinPrice) {
    return null;
  }

  if (coinRunAtDate && !ammRunning) {
    // Hidden for now
    return (
      <div className="hidden flex-wrap items-center gap-2">
        <Badge
          className="flex h-7 cursor-pointer items-center px-3 py-0"
          onClick={() =>
            setDialog(DIALOG.GetTokensEarly, {
              open: true,
              params: {
                username: data?.coinProperties.Profile.Username ?? "",
              },
            })
          }
        >
          <MarketCountdown
            className="cursor-pointer"
            openDate={coinRunAtDate}
          />
        </Badge>

        {tradeOnOpenfundButton}
      </div>
    );
  }

  const shouldShowQuickBuyButton =
    ammRunning && publicKey !== process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY;
  const shouldShowTradeOnOpenfundButton =
    ammRunning || (!data?.hasAmmConfigs && data?.hasCoin);

  const renderQuickBuyButton = () => {
    return (
      <div className="flex h-7 items-center">
        <Button
          size="xs"
          variant="default"
          className={`
            ${profileCoinActionButtonClassName} relative flex items-center
            gap-1 text-primary-foreground hover:bg-primary-foreground
            hover:text-primary
          `}
        >
          <div ref={coinPriceWrapperRef} className="flex items-center">
            <span>Quick Buy</span>
          </div>
          <InfoTooltip
            iconSize={16}
            iconColor="text-inherit"
            className="max-w-[300px] min-w-[300px]"
            text={
              <>
                <p className="mb-4" key="row-first">
                  Every user on {APP_NAME_GENERIC} can create a &quot;
                  {DESO_TOKEN_DISPLAY_NAME} Token&quot; associated with their
                  profile, which anyone can buy and sell. Creators can attach
                  content revenue and other benefits to their token, and the
                  price generally goes up and down as people buy and sell. You
                  can also lock creators' tokens to earn yield.
                </p>
                <p className="mb-4" key="row-second">
                  Creators make money on their token from trading fees, and from
                  optionally retaining a percentage of their token's initial
                  supply.
                </p>
                <p key="row-third">
                  You can hide this button from your profile in Settings.
                </p>
              </>
            }
          />
        </Button>
      </div>
    );
  };

  return (
    <div
      className="
        flex flex-wrap items-center gap-2
      "
    >
      {shouldShowQuickBuyButton && (
        <TriggerLoggedInModalWindow>
          <div
            onClickCapture={(event) => {
              event.stopPropagation();
              setDialog(DIALOG.TradeCoin, {
                open: true,
                params: {
                  publicKey,
                  tab: CreatorCoinTab.Buy,
                },
              });
            }}
          >
            {renderQuickBuyButton()}
          </div>
        </TriggerLoggedInModalWindow>
      )}
      {shouldShowTradeOnOpenfundButton && tradeOnOpenfundButton}
    </div>
  );
};

export default CreatorCoinExchangeWrapper;
